import { useEffect, useState } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { trainingApi } from '../api'
import type { TrainingProjection, TrainingProjectionStatus, UpdateTrainingRequest, TrainingSetDetailsResponse } from '../types/api'
import type { ApiError } from '../api/client'
import { useTranslation } from '../i18n/useTranslation'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from '../hooks/useTheme'
import { formatLongDate } from '../utils/dateTranslations'
import { Button } from './ui'
import { getInfoBannerStyle, getInputStyle, getSectionCardStyle, INPUT_CLASS_NAME, TEXTAREA_CLASS_NAME } from '../utils/themeStyles'

const FILTER_BG = { light: 'rgba(0, 0, 0, 0.05)', dark: 'rgba(255, 255, 255, 0.05)' }

export default function EditTraining() {
  const navigate = useNavigate()
  const location = useLocation()
  const { traineeId, setId, projectionId } = useParams<{ traineeId: string; setId: string; projectionId: string }>()
  const { t } = useTranslation()
  const theme = useTheme()
  const { user } = useAuth()
  const filterBg = theme.isDark ? FILTER_BG.dark : FILTER_BG.light

  const [training, setTraining] = useState<TrainingProjection | null>(null)
  const [trainingSetDetails, setTrainingSetDetails] = useState<TrainingSetDetailsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  // Form state
  const [status, setStatus] = useState<TrainingProjectionStatus>('ACTIVE')
  const [newDate, setNewDate] = useState('')
  const [newTime, setNewTime] = useState('')
  const [newDurationHours, setNewDurationHours] = useState(1)
  const [newDurationMinutes, setNewDurationMinutes] = useState(30)
  const [updateReason, setUpdateReason] = useState('')
  const [fired, setFired] = useState(false)

  // Status options: include ACTIVE only if current status is not ACTIVE and not MOVED (for undo functionality)
  // MOVED trainings should be moved back to original date instead of reverting to ACTIVE
  const STATUS_OPTIONS: TrainingProjectionStatus[] = training?.status === 'ACTIVE' || training?.status === 'MOVED'
    ? ['MOVED', 'SKIPPED', 'CANCELLED']
    : ['ACTIVE', 'MOVED', 'SKIPPED', 'CANCELLED']

  // Check if we came from ThisWeekTrainings and use that path, otherwise go to training set details
  const fromPath = (location.state as { fromPath?: string })?.fromPath
  const backPath = fromPath || `/trainee/${traineeId}/set/${setId}`

  useEffect(() => {
    const fetchTraining = async () => {
      if (!traineeId || !setId || !projectionId) return

      try {
        // Fetch training set details to get maxSkipCount and stats
        const [trainingsData, setDetails] = await Promise.all([
          trainingApi.getSetTrainings(parseInt(traineeId), parseInt(setId)),
          trainingApi.getTrainingSetDetails(parseInt(setId))
        ])
        
        setTrainingSetDetails(setDetails)
        
        // Check if current user is the trainee (compare with trainingSet.traineeId, not route param)
        // Route param can be coachId when coming from AllTrainingSets
        if (setDetails.trainingSet && user?.id && user.id === setDetails.trainingSet.traineeId) {
          navigate(backPath)
          return
        }
        
        const found = trainingsData.find(t => t.id === parseInt(projectionId))
        
        if (found) {
          setTraining(found)
          
          // Initialize form with training data
          const startDate = new Date(found.start)
          const endDate = new Date(found.end)
          const durationMs = endDate.getTime() - startDate.getTime()
          const durationHours = Math.floor(durationMs / (1000 * 60 * 60))
          const durationMinutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60))

          setStatus(found.status)
          setNewDate(startDate.toISOString().split('T')[0])
          setNewTime(startDate.toTimeString().slice(0, 5))
          setNewDurationHours(durationHours)
          setNewDurationMinutes(durationMinutes)
          setUpdateReason(found.comment || '')
          setFired(found.isFired || false)
        } else {
          alert('Training not found')
          navigate(backPath)
        }
      } catch (err) {
        console.error('Error fetching training:', err)
        alert(err instanceof Error ? err.message : 'Failed to load training')
      } finally {
        setLoading(false)
      }
    }

    fetchTraining()
  }, [traineeId, setId, projectionId, navigate, user?.id, backPath])

  const handleSubmit = async () => {
    if (!training || !setId || !projectionId) return

    const updates: UpdateTrainingRequest = {}

    const newStartTime = `${newDate}T${newTime}:00`
    const hasDateTimeChanged = newStartTime !== training.start
    
    // Handle status changes
    if (status === 'ACTIVE' && training.status !== 'ACTIVE') {
      // Reverting to ACTIVE: just set status, don't change date/time
      // The backend will handle restoring the original time if needed
      updates.status = 'ACTIVE'
    } else if (hasDateTimeChanged) {
      // If date/time changed, automatically set status to MOVED (unless already MOVED)
      updates.newStartTime = newStartTime
      if (status === 'ACTIVE') {
        updates.status = 'MOVED'
      } else {
        updates.status = status
      }
    } else if (status !== training.status) {
      // Only update status if date didn't change
      updates.status = status
    }

    const startDate = new Date(training.start)
    const endDate = new Date(training.end)
    const durationMs = endDate.getTime() - startDate.getTime()
    const oldDurationHours = Math.floor(durationMs / (1000 * 60 * 60))
    const oldDurationMinutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60))

    const newDurationStr = `PT${newDurationHours}H${newDurationMinutes}M`
    const oldDurationStr = `PT${oldDurationHours}H${oldDurationMinutes}M`
    if (newDurationStr !== oldDurationStr) {
      updates.newDuration = newDurationStr
    }

    if (updateReason !== (training.comment || '')) {
      updates.updateReason = updateReason
    }

    // Handle fired flag
    if (status === 'ACTIVE') {
      // When reverting to ACTIVE, always clear fired flag
      if (training.isFired) {
        updates.fired = false
      }
    } else if (status !== 'CANCELLED' && fired !== (training.isFired || false)) {
      // For other statuses (except CANCELLED), update fired if changed
      updates.fired = fired
    } else if (status === 'CANCELLED' && training.isFired) {
      // If changing to CANCELLED and training was fired, unset fired
      updates.fired = false
    }

    setSubmitting(true)
    try {
      await trainingApi.updateTraining(parseInt(setId), parseInt(projectionId), updates)
      
      alert(t.editTraining.success.updated)
      navigate(backPath)
    } catch (err) {
      console.error('Error updating training:', err)
      const apiError = err as ApiError
      alert(apiError?.message || 'Failed to update training')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-5"
      >
        <div className="text-center py-10 text-base" style={{ color: theme.text.muted }}>{t.common.loading}</div>
      </div>
    )
  }

  if (!training) {
    return (
      <div className="p-5">
        <div className="text-center py-10">
          <div className="text-4xl mb-2">⚠️</div>
          <p className="text-base" style={{ color: theme.text.muted }}>Training not found</p>
        </div>
      </div>
    )
  }

  const startDate = new Date(training.start)

  return (
    <div className="p-5">

      <header className="mb-6">
        <h1 className="text-2xl font-bold mb-1" style={{ color: theme.text.primary }}>{t.editTraining.title}</h1>
        <p className="text-sm" style={{ color: theme.text.muted }}>
          {formatLongDate(startDate, t)}
        </p>
      </header>

      <div className="space-y-5">
        {/* Status */}
        <div className="rounded-xl p-5" style={getSectionCardStyle(theme)}>
          <label className="block text-base font-semibold mb-3" style={{ color: theme.text.primary }}>{t.editTraining.form.status}</label>
          <div className="flex flex-col gap-2">
            {STATUS_OPTIONS.map((opt) => {
              const isSelected = status === opt

              const getStatusIcon = () => {
                switch (opt) {
                  case 'ACTIVE':
                    return '✅'
                  case 'MOVED':
                    return '📅'
                  case 'SKIPPED':
                    return '⏭️'
                  case 'CANCELLED':
                    return '❌'
                  default:
                    return ''
                }
              }

              const getStatusLabel = () => {
                switch (opt) {
                  case 'ACTIVE':
                    return t.editTraining.form.statusOptions.active
                  case 'MOVED':
                    return t.editTraining.form.statusOptions.moved
                  case 'SKIPPED':
                    return t.editTraining.form.statusOptions.skipped
                  case 'CANCELLED':
                    return t.editTraining.form.statusOptions.cancelled
                  default:
                    return ''
                }
              }

              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => {
                    if (status === opt) return
                    setStatus(opt)
                    // When reverting to ACTIVE, clear fired flag
                    if (opt === 'ACTIVE') {
                      setFired(false)
                    } else if (opt !== 'SKIPPED') {
                      setFired(false)
                    }
                  }}
                  className="py-3 px-4 rounded-lg text-sm font-semibold transition-all text-left flex items-center gap-2"
                  style={{
                    backgroundColor: isSelected ? theme.accent.light : filterBg,
                    border: isSelected 
                      ? `2px solid ${theme.accent.border}`
                      : '2px solid rgba(156, 163, 175, 0.2)',
                    opacity: isSelected ? 1 : 0.7,
                  }}
                >
                  <span className="text-lg">{getStatusIcon()}</span>
                  <span>{getStatusLabel()}</span>
                  {isSelected && <span className="ml-auto">✓</span>}
                </button>
              )
            })}
          </div>
        </div>

        {/* Skip limit info message - Only show when user selects SKIPPED (not if already skipped) */}
        {status === 'SKIPPED' && training?.status !== 'SKIPPED' && !fired && trainingSetDetails && training && (() => {
          const currentSkippedCount = trainingSetDetails.stats.skippedCount ?? 0
          const maxSkipCount = trainingSetDetails.trainingSet.maxSkipCount
          
          // Calculate planned skipped count: add 1 since this training is being newly skipped
          const plannedSkippedCount = currentSkippedCount + 1
          
          return (
            <div className="rounded-xl p-4" style={getInfoBannerStyle(theme)}>
              <div className="flex items-center gap-2">
                <span className="text-lg">ℹ️</span>
                <p className="text-xs leading-relaxed" style={{ color: theme.text.muted }}>
                  {maxSkipCount !== undefined && maxSkipCount !== null
                    ? t.editTraining.skipLimit.willBeSkipped
                        .replace('{{planned}}', plannedSkippedCount.toString())
                        .replace('{{max}}', maxSkipCount.toString())
                    : t.editTraining.skipLimit.countOnly
                        .replace('{{current}}', currentSkippedCount.toString())}
                </p>
              </div>
            </div>
          )
        })()}

        {/* Show date, time, and duration only for MOVED status */}
        {status === 'MOVED' && (
          <>
            {/* Date */}
            <div className="rounded-xl p-5" style={getSectionCardStyle(theme)}>
              <label className="block text-base font-semibold mb-3" style={{ color: theme.text.primary }}>{t.editTraining.form.date}</label>
              <input
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                className={INPUT_CLASS_NAME}
                style={getInputStyle(theme)}
              />
            </div>

            {/* Time */}
            <div className="rounded-xl p-5" style={getSectionCardStyle(theme)}>
              <label className="block text-base font-semibold mb-3" style={{ color: theme.text.primary }}>{t.editTraining.form.startTime}</label>
              <input
                type="time"
                value={newTime}
                onChange={(e) => setNewTime(e.target.value)}
                className={INPUT_CLASS_NAME}
                style={getInputStyle(theme)}
              />
            </div>

            {/* Duration */}
            <div className="rounded-xl p-5" style={getSectionCardStyle(theme)}>
              <label className="block text-base font-semibold mb-4" style={{ color: theme.text.primary }}>{t.editTraining.form.duration}</label>
              <div className="flex gap-4">
                <div className="flex-1">
                  <label className="block text-sm mb-2" style={{ color: theme.text.muted }}>{t.editTraining.form.hours}</label>
                  <input
                    type="number"
                    min="0"
                    max="5"
                    value={newDurationHours}
                    onChange={(e) => setNewDurationHours(parseInt(e.target.value) || 0)}
                    className={INPUT_CLASS_NAME}
                    style={getInputStyle(theme)}
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm mb-2" style={{ color: theme.text.muted }}>{t.editTraining.form.minutes}</label>
                  <input
                    type="number"
                    min="0"
                    max="59"
                    step="15"
                    value={newDurationMinutes}
                    onChange={(e) => setNewDurationMinutes(parseInt(e.target.value) || 0)}
                    className={INPUT_CLASS_NAME}
                    style={getInputStyle(theme)}
                  />
                </div>
              </div>
            </div>
          </>
        )}

        {/* Fired Checkbox - Only for SKIPPED status, or for other statuses if already fired */}
        {(status === 'SKIPPED' || (status !== 'CANCELLED' && training?.isFired === true)) && (
          <div className="rounded-xl p-5" style={getSectionCardStyle(theme)}>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={fired}
                onChange={(e) => setFired(e.target.checked)}
                className="w-5 h-5 cursor-pointer"
                style={{ accentColor: theme.buttonColor || '#0088cc' }}
              />
              <span className="text-base font-semibold" style={{ color: theme.text.primary }}>{t.editTraining.form.markAsFired}</span>
            </label>
            <p className="text-xs mt-2 ml-8" style={{ color: theme.text.muted }}>
              {t.editTraining.form.firedDescription}
            </p>
          </div>
        )}

        {/* Update Reason */}
        <div className="rounded-xl p-5" style={getSectionCardStyle(theme)}>
          <label className="block text-base font-semibold mb-3" style={{ color: theme.text.primary }}>{t.editTraining.form.notes}</label>
          <p className="text-xs mb-3 flex items-center gap-1" style={{ color: theme.text.muted }}>
            <span>👁️</span>
            <span>{t.editTraining.form.notesVisibleToTrainee}</span>
          </p>
          <textarea
            value={updateReason}
            onChange={(e) => setUpdateReason(e.target.value)}
            rows={4}
            maxLength={200}
            className={TEXTAREA_CLASS_NAME}
            style={getInputStyle(theme)}
            placeholder={t.editTraining.form.notesPlaceholder}
          />
        </div>

        {/* Notification Info */}
        <div className="rounded-xl p-4" style={getInfoBannerStyle(theme)}>
          <div className="flex items-center gap-2">
            <span className="text-lg">🔔</span>
            <p className="text-xs leading-relaxed" style={{ color: theme.text.muted }}>
              {t.editTraining.notificationInfo}
            </p>
          </div>
        </div>

        {/* Buttons */}
        <div className="space-y-3">
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            variant="primary"
            size="lg"
            fullWidth
          >
            {submitting ? t.editTraining.button.saving : t.editTraining.button.save}
          </Button>
          <Button
            onClick={() => navigate(backPath)}
            disabled={submitting}
            variant="secondary"
            size="lg"
            fullWidth
          >
            {t.editTraining.button.cancel}
          </Button>
        </div>
      </div>
    </div>
  )
}
